import type { StateTree } from '@makemydeal/dr-dash-types';
import type { paymentServicesTypes } from '@makemydeal/dr-platform-types';
import { LEASE } from '@makemydeal/dr-platform-types';
import { selectors, utils } from '@makemydeal/dr-offer-redux';
import { getAccessoriesList } from './accessoriesSelectors';
import { getVppSelectedProducts } from './vehicleProtectionSelectors';
import {
    getCapReductionTaxCapitalizedIndicator,
    getCurrentOfferType,
    getTaxItemsWithAdjustedRates,
    isTaxCapped
} from './offerRedux';

/**
 * Deal-level context needed to label the retention tax rows (Upfront/Capitalised only applies to
 * lease deals). Kept separate from the raw tax items so the display composition can live with the
 * other item categories in the change-vehicle activity (buildCategories).
 */
export interface RetentionTaxContext {
    isLease: boolean;
    capped: boolean;
    crtCapped: boolean;
}

/**
 * The incentive rows the retention modal offers, and the list the retention telemetry is counted
 * against (`getRetentionTotals` in `changeVehicleMiddleware`), so the two cannot disagree.
 *
 * Mirrors the Programs & Incentives slide-out: `getAppliedIncentives` merges program/catalog incentives
 * with the user-added manual ones (so catalog programs are not lost). On initial load the persisted
 * manual entries can still lack a `code`, so the offer-redux merge can emit the same incentive twice
 * (once program-sourced, once manual). Dedupe on `code` (populated for every returned entry via
 * `transformToBaseIncentive` => code || pdId), preferring the program-sourced row (manual === false) so
 * its real source label is retained.
 *
 * Manufacturer (standard) program incentives are dropped before that dedupe (US2062556). A standard is
 * quoted against the vehicle it belongs to, so the switch resolves the new vehicle's own standards
 * instead of carrying the old vehicle's over, and a "Keep" decision on one was never acted on. This is
 * deliberately not gated on `enableProgramIncentivesRestoration_F292564`: with restoration off
 * `buildOfferIncentivesNode` (`paymentPayloadUtils`) returns early with no `nonConditional` key and no
 * `excludeStdIncs`, which is the shape `includeStandardIncentives` produces for MV, so Payment Services
 * resolves the new vehicle's standards in both toggle states. With restoration off the row was worse
 * than ignored: `getManualIncentivesToRetain` passes `retained.incentives` through wholesale, so a
 * checked standard went out on the manual-incentive node and landed on the new vehicle as a dealer
 * rebate on top of its own standards.
 *
 * Dropping before the dedupe is what stops a standard from claiming the key of the dealer-entered row
 * it duplicates and removing the one row the dealer can actually control.
 */
export const getIncentivesForRetentionModal = (state: StateTree) => {
    const applied = (selectors.getAppliedIncentives(state) ?? []).filter(
        (incentive) => !utils.incentives.isProgramStandardIncentive(incentive)
    );
    const byKey = new Map<string, (typeof applied)[number]>();
    for (const incentive of applied) {
        const key = String(incentive.code ?? `${incentive.name}-${incentive.type}`);
        const existing = byKey.get(key);
        if (!existing || (existing.manual !== false && incentive.manual === false)) {
            byKey.set(key, incentive);
        }
    }
    return Array.from(byKey.values());
};

export const getAccessoriesForRetentionModal = (state: StateTree) => {
    return getAccessoriesList(state);
};

export const getVppForRetentionModal = (state: StateTree) => {
    return getVppSelectedProducts(state);
};

/**
 * Returns the tax items that carry a manual percentage (rate) override, suitable both for display
 * and for direct inclusion in the retention payload. Amount-only overrides are excluded so the
 * Taxes section is hidden when nothing had its rate changed (US2035248). The row/label composition
 * happens in the change-vehicle activity (buildCategories), alongside the other item categories.
 */
export const getManuallyOverriddenTaxItems = (state: StateTree): paymentServicesTypes.TaxItem[] => {
    return getTaxItemsWithAdjustedRates(state).filter((taxItem) => taxItem.isManualTax && !taxItem.isManualTaxAmount);
};

/** Deal-level flags used to label the retention tax rows (see RetentionTaxContext). */
export const getRetentionTaxContext = (state: StateTree): RetentionTaxContext => ({
    isLease: getCurrentOfferType(state) === LEASE,
    capped: isTaxCapped(state),
    crtCapped: getCapReductionTaxCapitalizedIndicator(state)
});
